import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { EkvDashboardReq } from '../../model/inko-backend.model'

/* Model */

export interface EkvDashboardStateModel {
  version: number
  ekvColumns: {
    /**
     * All available column keys (in order)
     */
    keys: string[]
    /**
     * Currently visible column keys
     */
    visible: string[]
  }
  ekvFilter: EkvDashboardReq
}

// EKV

// Seit 2026-10-12 fuenf Spalten statt elf (Liste „Nacharbeit zum Kostenvoranschlag“): was zu tun ist
// und seit wann. Herkunft, Status, Antragsart und Vorgangsnummer stehen in der Versorgung.
export const ekvAvailableColumns: { label: string; key: string; defaultVisible?: boolean }[] = [
  { label: 'Patient:in', key: 'patient', defaultVisible: true },
  { label: 'Versorgung', key: 'versorgung', defaultVisible: true },
  { label: 'Krankenkasse', key: 'krankenkasse', defaultVisible: true },
  { label: 'Was zu tun ist', key: 'aufgabe', defaultVisible: true },
  // Seit 2026-10-21: die Notizen der Versorgung (an der Patient:in), wie die Notiz-Spalte der Logistik.
  { label: 'Notiz', key: 'notiz', defaultVisible: true },
  { label: 'Offen seit', key: 'offenSeit', defaultVisible: true },
]
export const ekvColumnNameMap = ekvAvailableColumns.reduce(
  (acc, col) => {
    acc[col.key] = col.label
    return acc
  },
  {} as Record<string, string>,
)

/* Actions */

// Ekv

/**
 * Update the PHM columns from the available columns
 */
export class DashboardStateEkvUpdateColumns {
  static readonly type = '[Dashboard] Update Ekv Columns'
}

export class DashboardStateEkvMoveColumns {
  static readonly type = '[Dashboard] Move Ekv Columns'
  constructor(
    public indexA: number,
    public indexB: number,
  ) {}
}

export class DashboardStateEkvSetColumnVisible {
  static readonly type = '[Dashboard] Visible Ekv Column'
  constructor(public keys: string[]) {}
}

export class DashboardStateEkvReset {
  static readonly type = '[Dashboard] Reset Ekv Columns'
}

export class DashboardStateEkvSetFilter {
  static readonly type = '[Dashboard] Set Ekv Filter'
  constructor(public filter: Partial<EkvDashboardReq>) {}
}
export class DashboardStateEkvResetFilter {
  static readonly type = '[Dashboard] Reset Ekv Filter'
}

/* State */

const defaultState: EkvDashboardStateModel = {
  // 2: neue Spalten der Nacharbeit-Liste (2026-10-12), der gespeicherte Stand faellt auf den Standard zurueck.
  version: 2,

  ekvColumns: {
    keys: ekvAvailableColumns.map((c) => c.key),
    visible: ekvAvailableColumns.filter((c) => c.defaultVisible).map((c) => c.key),
  },
  ekvFilter: {},
}

@State<EkvDashboardStateModel>({
  name: 'ekvDashboard',
  defaults: defaultState,
})
@Injectable()
export class EkvDashboardState {
  // Ekv

  @Selector()
  static ekvAvailableColumns(state: EkvDashboardStateModel) {
    return state.ekvColumns.keys
  }
  @Selector()
  static ekvVisibleColumns(state: EkvDashboardStateModel) {
    return state.ekvColumns.visible
  }
  @Selector()
  static ekvFilterCounter() {
    return 0
  }
  @Selector()
  static ekvFilter(state: EkvDashboardStateModel) {
    return state.ekvFilter
  }

  // Ekv
  @Action(DashboardStateEkvUpdateColumns)
  setEkvColumns(ctx: StateContext<EkvDashboardStateModel>) {
    const { version } = ctx.getState()
    if (version != defaultState.version) {
      ctx.setState(defaultState)
      return
    }

    const columns = ctx.getState().ekvColumns.keys.slice()
    const allColumnKeys = ekvAvailableColumns.map((c) => c.key)

    // Add any new columns that are not already in the state
    allColumnKeys.forEach((key) => {
      if (!columns.includes(key)) {
        columns.push(key)
      }
    })
    // remove any columns that are no longer available
    const filteredColumns = columns.filter((key) => allColumnKeys.includes(key))

    ctx.patchState({
      ekvColumns: {
        ...ctx.getState().ekvColumns,
        keys: filteredColumns,
      },
    })
  }

  @Action(DashboardStateEkvMoveColumns)
  moveEkvColumns(ctx: StateContext<EkvDashboardStateModel>, action: DashboardStateEkvMoveColumns) {
    const columns = ctx.getState().ekvColumns.keys.slice()
    const [movedColumn] = columns.splice(action.indexA, 1)
    columns.splice(action.indexB, 0, movedColumn)
    ctx.patchState({
      ekvColumns: {
        ...ctx.getState().ekvColumns,
        keys: columns,
      },
    })
  }

  @Action(DashboardStateEkvSetColumnVisible)
  setEkvColumnVisible(ctx: StateContext<EkvDashboardStateModel>, action: DashboardStateEkvSetColumnVisible) {
    ctx.patchState({
      ekvColumns: {
        ...ctx.getState().ekvColumns,
        visible: action.keys,
      },
    })
  }

  @Action(DashboardStateEkvReset)
  resetEkvColumns(ctx: StateContext<EkvDashboardStateModel>) {
    ctx.patchState({
      ekvColumns: defaultState.ekvColumns,
    })
  }

  @Action(DashboardStateEkvSetFilter)
  setEkvFilter(ctx: StateContext<EkvDashboardStateModel>) {
    ctx.patchState({
      ekvFilter: {
        ...ctx.getState().ekvFilter,
        // TODO: apply action.filter (re-add the action param when implementing)
      },
    })
  }

  @Action(DashboardStateEkvResetFilter)
  resetEkvFilter(ctx: StateContext<EkvDashboardStateModel>) {
    ctx.patchState({
      ekvFilter: defaultState.ekvFilter,
    })
  }
}
